import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  UnitTransferEscrowApprovalRoleEnumType,
  UnitTransferEscrowExceptionStatusEnumType,
} from '__generated__/graphql';
import dayjs from 'dayjs';
import { FC } from 'react';

import Badge from 'primitives/Badge';
import ConfirmButton from 'primitives/ConfirmButton';

import SectionCard from 'components/SectionCard';

import unitTransferEscrowExceptionStatusToReadable from 'utils/enums/unit-transfer-escrow-exception-status-to-readable';

const APPROVE_ESCROW_EXCEPTION_MUTATION = gql(`
  mutation ApproveEscrowException($requestId: ID!, $role: UnitTransferEscrowApprovalRoleEnumType!) {
    approveEscrowException(requestId: $requestId, role: $role) {
      id
      escrowException {
        id
        status
        opsApprovedAt
        legalApprovedAt
      }
    }
  }
`);

const DECLINE_ESCROW_EXCEPTION_MUTATION = gql(`
  mutation DeclineEscrowException($requestId: ID!) {
    declineEscrowException(requestId: $requestId) {
      id
      status
      escrowException { id status declinedByUserId }
    }
  }
`);

type EscrowException = {
  id: string;
  status: UnitTransferEscrowExceptionStatusEnumType;
  note?: string | null;
  opsApprovedAt?: string | null;
  legalApprovedAt?: string | null;
};

// This is a dual sign-off, not a single approval — OPS_APPROVED is HALF approved, so it must
// never be rendered as a plain "Approved" badge (see unit-transfer-escrow-exception-status-to-readable).
const EscrowExceptionPanel: FC<{ requestId: string; escrowException: EscrowException }> = ({
  requestId,
  escrowException,
}) => {
  const [approveEscrowException, { loading: approving, error: approveError }] = useMutation(
    APPROVE_ESCROW_EXCEPTION_MUTATION
  );
  const [declineEscrowException, { loading: declining, error: declineError }] = useMutation(
    DECLINE_ESCROW_EXCEPTION_MUTATION
  );

  const { label, color } = unitTransferEscrowExceptionStatusToReadable(escrowException.status);
  const canDecline =
    escrowException.status !== UnitTransferEscrowExceptionStatusEnumType.Approved &&
    escrowException.status !== UnitTransferEscrowExceptionStatusEnumType.Declined;

  return (
    <SectionCard
      title="Escrow Exception"
      subTitle="Requires both an ops and a legal sign-off before the request can proceed"
      bodyClassName="flex flex-col gap-y-3 px-4 py-3 sm:p-4"
    >
      <>
        <div className="flex items-center gap-x-2">
          <Badge label={label} color={color} showIndicator />
        </div>
        {escrowException.note && (
          <p className="text-sm text-gray-700">Note: {escrowException.note}</p>
        )}
        <div className="text-sm text-gray-500">
          {escrowException.opsApprovedAt && (
            <p>Ops approved {dayjs(escrowException.opsApprovedAt).format('D MMM YYYY, h:mm A')}</p>
          )}
          {escrowException.legalApprovedAt && (
            <p>
              Legal approved {dayjs(escrowException.legalApprovedAt).format('D MMM YYYY, h:mm A')}
            </p>
          )}
        </div>
        <div className="flex flex-row gap-2">
          {escrowException.status === UnitTransferEscrowExceptionStatusEnumType.Pending && (
            <ConfirmButton
              loading={approving}
              error={approveError}
              description="Approve this escrow exception as Ops? Legal approval will still be required."
              onConfirm={() =>
                approveEscrowException({
                  variables: { requestId, role: UnitTransferEscrowApprovalRoleEnumType.Ops },
                })
              }
            >
              Approve (Ops)
            </ConfirmButton>
          )}
          {escrowException.status === UnitTransferEscrowExceptionStatusEnumType.OpsApproved && (
            <ConfirmButton
              loading={approving}
              error={approveError}
              description="Approve this escrow exception as Legal? This will fully approve the exception."
              onConfirm={() =>
                approveEscrowException({
                  variables: { requestId, role: UnitTransferEscrowApprovalRoleEnumType.Legal },
                })
              }
            >
              Approve (Legal)
            </ConfirmButton>
          )}
          {canDecline && (
            <ConfirmButton
              variant="secondary"
              loading={declining}
              error={declineError}
              description="Decline this escrow exception? This will reject the unit transfer request."
              onConfirm={() => declineEscrowException({ variables: { requestId } })}
            >
              Decline
            </ConfirmButton>
          )}
        </div>
      </>
    </SectionCard>
  );
};

export default EscrowExceptionPanel;
